/**
 * Reviewing a social post: a plain 1 to 5 score with the reasons, the brand check, applying a suggested change, and when to send.
 *
 * Pure. The worker hands in what it knows (the post, the person's earlier decisions, the posts already planned) and this
 * returns the words and times shown on the review screen. Nothing is guessed: with too few decisions there is no score and
 * we say so, and the times are labelled as general B2B guidance until the account has its own results.
 */
import { z } from "zod";
import { brandProblemText, brandRulesBroken, type NeverSayRule } from "./adsBrand.ts";
import { localParts } from "./adsSchedule.ts";
import { postFormat, postHook, type PostFormat, type PostHook } from "./socialStage.ts";

// ----- score -----

export const SCORE_MIN_DECISIONS = 3;
const TOPIC_REPEAT_AT = 3;

export interface PastDecision {
  hook: PostHook;
  format: PostFormat;
  topic: string | null;
  approved: boolean;
}

export interface ScoreInput {
  text: string;
  imageUrls: readonly string[];
  videoUrl: string | null;
  topic: string | null;
  /** The person's decisions over the last eight weeks: approved, or "Not for us". */
  history: readonly PastDecision[];
  /** Other posts on the same topic in the last 30 days, not counting this one. */
  sameTopicLast30Days: number;
  /** Learned rules a manager switched off (see socialAgents.ts). Their group of past decisions is not counted. */
  offRules?: readonly string[];
}

export interface PostScore {
  /** 1 to 5, or null when there is not enough to say. */
  value: number | null;
  ups: string[];
  downs: string[];
  note: string | null;
}

const HOOK_WORD: Record<PostHook, string> = { Number: "a number", Question: "a question", Statement: "a statement" };

const rate = (rows: readonly PastDecision[]) => ({ n: rows.length, ok: rows.filter((r) => r.approved).length });

/**
 * Starts at 3. How the post opens and its format each move it one step by what the person approved before (needs three
 * earlier posts like it). A topic posted three or more times in 30 days moves it down one. Never below 1 or above 5.
 */
export function scorePost(i: ScoreInput): PostScore {
  const ups: string[] = [];
  const downs: string[] = [];
  const repeated = i.topic !== null && i.sameTopicLast30Days >= TOPIC_REPEAT_AT;
  if (i.history.length < SCORE_MIN_DECISIONS && !repeated) {
    return { value: null, ups, downs, note: `Not scored yet. We need ${SCORE_MIN_DECISIONS} of your decisions first, and you have made ${i.history.length}.` };
  }
  let value = 3;
  const hook = postHook(i.text);
  const format = postFormat({ imageUrls: i.imageUrls, videoUrl: i.videoUrl });

  const off = new Set(i.offRules ?? []);
  const h = rate(off.has(`hook:${hook}`) ? [] : i.history.filter((p) => p.hook === hook));
  if (h.n >= SCORE_MIN_DECISIONS) {
    if (h.ok / h.n >= 0.75) { value += 1; ups.push(`Opens with ${HOOK_WORD[hook]}. You approved ${h.ok} of ${h.n} like it.`); }
    else if (h.ok / h.n <= 0.34) { value -= 1; downs.push(`Opens with ${HOOK_WORD[hook]}. You approved only ${h.ok} of ${h.n} like it.`); }
  }
  const f = rate(off.has(`format:${format}`) ? [] : i.history.filter((p) => p.format === format));
  if (f.n >= SCORE_MIN_DECISIONS) {
    if (f.ok / f.n >= 0.75) { value += 1; ups.push(`${format} posts: you approved ${f.ok} of ${f.n}.`); }
    else if (f.ok / f.n <= 0.34) { value -= 1; downs.push(`${format} posts: you approved only ${f.ok} of ${f.n}.`); }
  }
  if (repeated) { value -= 1; downs.push(`${i.topic} has been posted ${i.sameTopicLast30Days} times in the last 30 days.`); }
  return { value: Math.max(1, Math.min(5, value)), ups, downs, note: ups.length + downs.length === 0 ? "Nothing in your past decisions pushes this up or down." : null };
}

// ----- brand check -----

export interface BrandCheck {
  /** ok: checked and clean. broken: uses words the brand kit says never to say. none: there is no kit to check against. */
  state: "ok" | "broken" | "none";
  lines: string[];
}

export function brandCheckPost(text: string, kitName: string | null, neverSay: readonly NeverSayRule[] | null): BrandCheck {
  if (kitName === null || neverSay === null) return { state: "none", lines: ["No brand kit is set for this channel, so nothing was checked."] };
  const broken = brandRulesBroken(text, neverSay);
  if (broken.length === 0) return { state: "ok", lines: [`Follows the ${kitName} brand kit.`] };
  return { state: "broken", lines: broken.map((r) => brandProblemText(r)) };
}

// ----- suggested change -----

/** Replaces the first exact match. Returns null when the words are no longer there, so a stale suggestion cannot be applied. */
export function applySuggestion(text: string, from: string, to: string): string | null {
  if (from === "") return null;
  const at = text.indexOf(from);
  return at === -1 ? null : `${text.slice(0, at)}${to}${text.slice(at + from.length)}`;
}

export const SUGGESTION_MAX = 2000;

// ----- when to send -----

export interface SendTime {
  /** 1 = Monday … 7 = Sunday, as in the weekly ad run. */
  weekday: number;
  /** Minutes after midnight, site time. */
  minutes: number;
}

/** Mid-week mornings first. General B2B guidance, not this account's own data. */
export const GENERAL_BEST_TIMES: readonly SendTime[] = [
  { weekday: 3, minutes: 10 * 60 },
  { weekday: 2, minutes: 10 * 60 },
  { weekday: 4, minutes: 10 * 60 },
];
/** Where "next free slot" looks: weekdays, three times a day. */
export const GENERAL_SLOT_GRID: readonly SendTime[] = [1, 2, 3, 4, 5].flatMap((weekday) => [9 * 60, 12 * 60, 15 * 60].map((minutes) => ({ weekday, minutes })));
export const GENERAL_TIMES_NOTE = "Mid-week mornings, 10 AM to noon, are a strong general time for B2B posts in the US and Canada. We switch to your own results once we have 30 days of them.";
export const SLOT_LEAD_MS = 60 * 60_000;
export const SLOT_CLASH_MS = 60 * 60_000;

/** The instant when the clock in `timeZone` shows `date` at `minutes`. Handles daylight saving by checking its own answer. */
export function zonedToUtc(date: string, minutes: number, timeZone: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const wanted = Date.UTC(y, m - 1, d, 0, minutes);
  let guess = wanted;
  for (let i = 0; i < 3; i += 1) {
    const p = localParts(new Date(guess), timeZone);
    const [py, pm, pd] = p.date.split("-").map(Number) as [number, number, number];
    const shown = Date.UTC(py, pm - 1, pd, 0, p.minutes);
    const off = shown - wanted;
    if (off === 0) break;
    guess -= off;
  }
  return new Date(guess);
}

/** Every slot of `times` in the next `days` days that is at least an hour away, earliest first. */
export function upcomingSlots(now: Date, timeZone: string, times: readonly SendTime[], days = 28): Date[] {
  const out: Date[] = [];
  const today = localParts(now, timeZone).date;
  const base = new Date(`${today}T12:00:00Z`);
  for (let k = 0; k <= days; k += 1) {
    const day = new Date(base.getTime() + k * 86_400_000);
    const date = day.toISOString().slice(0, 10);
    const weekday = ((day.getUTCDay() + 6) % 7) + 1;
    for (const t of times) {
      if (t.weekday !== weekday) continue;
      const at = zonedToUtc(date, t.minutes, timeZone);
      if (at.getTime() >= now.getTime() + SLOT_LEAD_MS) out.push(at);
    }
  }
  return out.sort((a, b) => a.getTime() - b.getTime());
}

const isFree = (at: Date, taken: readonly string[]) => taken.every((t) => Math.abs(Date.parse(t) - at.getTime()) >= SLOT_CLASH_MS);

export const sendOptionSchema = z.object({ id: z.enum(["suggested", "next_free"]), label: z.string(), at: z.string() });
export type SendOption = z.infer<typeof sendOptionSchema>;

/**
 * Two choices for the review screen: the best time that is free, and the earliest free slot. `taken` is when the same
 * channel already has a post planned. When both land on the same time, only the first is returned.
 */
export function sendOptions(now: Date, timeZone: string, taken: readonly string[], best: readonly SendTime[] = GENERAL_BEST_TIMES, grid: readonly SendTime[] = GENERAL_SLOT_GRID): SendOption[] {
  const fmt = (d: Date) => new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(d);
  const bestFirst = best.flatMap((b) => upcomingSlots(now, timeZone, [b])).filter((d) => isFree(d, taken)).sort((a, b) => a.getTime() - b.getTime())[0];
  // The suggested time is the earliest free slot among the best times, so a person is never sent weeks ahead.
  const free = upcomingSlots(now, timeZone, grid).find((d) => isFree(d, taken));
  const out: SendOption[] = [];
  if (bestFirst) out.push({ id: "suggested", label: fmt(bestFirst), at: bestFirst.toISOString() });
  if (free && (!bestFirst || free.getTime() !== bestFirst.getTime())) out.push({ id: "next_free", label: fmt(free), at: free.toISOString() });
  return out;
}

// ----- what the review screen receives -----

export const COMMENT_KINDS = ["comment", "suggestion"] as const;
export const commentKindSchema = z.enum(COMMENT_KINDS);

export const reviewCommentSchema = z.object({
  id: z.string(),
  kind: commentKindSchema,
  author: z.string(),
  body: z.string(),
  /** For a suggestion: the words to replace and what to put there. */
  from: z.string().nullable(),
  to: z.string().nullable(),
  status: z.enum(["open", "accepted", "declined"]),
  createdAt: z.string(),
});
export type ReviewComment = z.infer<typeof reviewCommentSchema>;

export const addCommentBodySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("comment"), body: z.string().trim().min(1).max(1000) }),
  z.object({ kind: z.literal("suggestion"), from: z.string().min(1).max(SUGGESTION_MAX), to: z.string().max(SUGGESTION_MAX), body: z.string().trim().max(1000).default("") }),
]);
export const answerSuggestionBodySchema = z.object({ answer: z.enum(["accept", "decline"]) });
export const scheduleBodySchema = z.object({ scheduledFor: z.string().datetime({ offset: true }), updatedAt: z.string().optional() });

export const socialReviewSchema = z.object({
  postId: z.string(),
  score: z.object({ value: z.number().int().min(1).max(5).nullable(), ups: z.array(z.string()), downs: z.array(z.string()), note: z.string().nullable() }),
  brand: z.object({ state: z.enum(["ok", "broken", "none"]), lines: z.array(z.string()) }),
  comments: z.array(reviewCommentSchema),
  when: z.object({ options: z.array(sendOptionSchema), note: z.string(), timeZone: z.string() }),
});
export type SocialReview = z.infer<typeof socialReviewSchema>;
